import {
  initTelegramSDK,
  initData,
  backButton,
  mainButton,
  miniApp,
  themeParams,
  viewport,
  hapticFeedback,
  swipeBehavior,
  openLink,
  openTelegramLink,
  requestWriteAccess,
} from './telegramSdk.js';

// Deadline for the viewport mount's bridge round-trip. Generous enough for a
// cold Telegram client, short enough that a silent client degrades the boot
// instead of stopping it.
const VIEWPORT_MOUNT_TIMEOUT_MS = 5000;

// How long Telegram's "allow the bot to message you" popup may stay unanswered.
const WRITE_ACCESS_TIMEOUT_MS = 120_000;

let _initialized = false;

export function createTmaAdapter() {
  /**
   * Run one optional boot step in isolation.
   *
   * Every step below is best-effort. A Telegram client that does not support a
   * method, or an SDK guard that throws, must not take the rest of the boot
   * down with it: before this, ONE throw (the `CSSVarsBoundError` raised by a
   * second `viewport.bindCssVars()` — sdk 3.9.2's `isAvailable()` does not
   * check the bound state) skipped the header colour, the theme vars AND the
   * swipe guard, and left `_initialized` false.
   */
  function step(name, fn) {
    try {
      // Every step here is synchronous today. The thenable guard is for the
      // day one of them is not: an async callback's rejection would otherwise
      // sail straight past this catch as an unhandled rejection, which is
      // exactly the isolation hole this helper exists to close.
      const result = fn();
      if (typeof result?.catch === 'function') {
        result.catch((error) => console.error(`[Platform/TMA] ${name} failed:`, error));
      }
    } catch (error) {
      console.error(`[Platform/TMA] ${name} failed:`, error);
    }
  }

  /**
   * Viewport: mount, bind the CSS vars, then EXPAND.
   *
   * `expand()` is the fix for the clipped-CTA reports. Telegram lays the
   * WebView out at its maximum height but only shows the visible area; until
   * the Mini App is expanded, the lower band of the page — where every sheet
   * CTA lives — sits off-screen, and no amount of in-sheet scrolling can reach
   * it. It is a no-op once the Mini App is already expanded.
   *
   * Every guard is a signal (a function), so it is called, not read.
   */
  async function initViewport() {
    try {
      if (viewport.mount.isAvailable() && !viewport.isMounted() && !viewport.isMounting()) {
        // The timeout is not cosmetic. mount() awaits a reply from the Telegram
        // client over the bridge and the SDK applies no deadline of its own, so
        // a client that never answers leaves this await pending forever — and a
        // pending promise is something the catch below cannot see. That would
        // park the whole f7ready boot: initAuth never runs, isReady never flips,
        // and the member sits on the loading screen with nothing in the console.
        // On timeout it rejects, which lands in the catch like any other failure.
        await viewport.mount({ timeout: VIEWPORT_MOUNT_TIMEOUT_MS });
      }
    } catch (error) {
      console.error('[Platform/TMA] viewport.mount failed:', error);
    }

    step('viewport.bindCssVars', () => {
      if (viewport.isCssVarsBound()) return;
      if (!viewport.bindCssVars.isAvailable()) {
        // bindCssVars requires a mounted viewport, so this is the visible
        // consequence of a mount that did not land: the
        // --tg-viewport-*-safe-area-inset-* vars stay undefined and the CSS
        // falls back to 0px. Say it out loud — it is the difference between a
        // clipped CTA with a cause and one without.
        console.warn('[Platform/TMA] viewport.bindCssVars unavailable — CSS vars not bound');
        return;
      }
      viewport.bindCssVars();
    });

    step('viewport.expand', () => {
      if (viewport.expand.isAvailable()) viewport.expand();
    });

    // Two lines, deliberately. `expand()` posts `web_app_expand` and returns —
    // the geometry signals only move when Telegram answers with
    // `viewport_changed`, so reading isExpanded() on the next statement reports
    // the value from BEFORE the expand. A boot-time `isExpanded=false` says
    // nothing. The `settled` line is the one device QA should read.
    step('viewport diagnostics', () => {
      console.log(
        `[Platform/TMA] viewport isExpanded=${viewport.isExpanded()} ` +
          `stableHeight=${viewport.stableHeight()} height=${viewport.height()}`,
      );
      if (typeof viewport.isExpanded.sub === 'function') {
        // `true` = subscribe once; the signal drops the listener after it fires.
        viewport.isExpanded.sub((isExpanded) => {
          console.log(
            `[Platform/TMA] viewport settled isExpanded=${isExpanded} ` +
              `stableHeight=${viewport.stableHeight()} height=${viewport.height()}`,
          );
        }, true);
      }
    });
  }

  async function initialize() {
    if (_initialized) return;

    // Set FIRST, before anything that can throw: TMA-scoped CSS keys off this
    // class on <html>, and it must be present even on a degraded boot.
    document.documentElement.classList.add('telegram-webapp');

    try {
      initTelegramSDK();

      step('backButton.mount', () => {
        if (!backButton.isMounted()) backButton.mount();
      });
      step('mainButton.mount', () => {
        if (!mainButton.isMounted()) mainButton.mount();
      });
      step('miniApp.mountSync', () => {
        if (!miniApp.isMounted()) miniApp.mountSync();
      });
      step('themeParams.mountSync', () => {
        if (!themeParams.isMounted()) themeParams.mountSync();
      });
      step('initData.restore', () => initData.restore());

      await initViewport();

      step('miniApp.setHeaderColor', () => miniApp.setHeaderColor('#31006F'));

      step('miniApp.bindCssVars', () => {
        if (miniApp.bindCssVars.isAvailable() && !miniApp.isCssVarsBound()) {
          miniApp.bindCssVars();
        }
      });

      step('themeParams.bindCssVars', () => {
        if (themeParams.bindCssVars.isAvailable() && !themeParams.isCssVarsBound()) {
          themeParams.bindCssVars();
        }
      });

      // `disableVertical.isAvailable()` requires the component to be MOUNTED
      // (SDK wrapSafe check 4). Without the mount() below it silently returned
      // false on every session, so a pull-down inside an open sheet could
      // collapse the whole Mini App — which reads to a member as "the button
      // disappeared".
      step('swipeBehavior guard', () => {
        if (swipeBehavior.mount.isAvailable() && !swipeBehavior.isMounted()) {
          swipeBehavior.mount();
        }
        if (swipeBehavior.disableVertical.isAvailable()) {
          swipeBehavior.disableVertical();
        }
      });

      _initialized = true;
      console.log('[Platform/TMA] Initialized');
    } catch (error) {
      // Only a throw from initTelegramSDK() itself reaches here — every other
      // step is isolated. `_initialized` stays false so a later call can retry.
      console.error('[Platform/TMA] Initialization failed:', error);
    }
  }

  function getInitData() {
    try {
      return initData.raw() ?? null;
    } catch {
      return null;
    }
  }

  function getTelegramUserId() {
    try {
      return initData.user()?.id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Open a URL OUTSIDE the Mini App, in a real browser (the Mini App stays open).
   *
   * `browser` is Telegram's `try_browser` preference ('chrome', 'firefox', …).
   * On Android the client then sends the link straight to that browser's
   * package and skips both Custom Tabs and its own in-app WebView browser —
   * which is where the plain call lands for a member who keeps Telegram's
   * in-app browser on, and where a PWA can never be installed. A missing
   * package fails silently (no callback), so the caller offers a plain second
   * button with no preference. iOS always opens the system browser and the
   * preference changes nothing there; callers pass none on iOS.
   *
   * The option names are the SDK's camelCase ones: `openLink` maps ONLY
   * `tryBrowser` / `tryInstantView` onto the bridge's `try_browser` /
   * `try_instant_view` (sdk 3.9.2, dist/index.js). The snake_case key this
   * passed before was dropped, so no preference ever reached Telegram.
   *
   * Never hand this an `intent://` URL. Telegram Android refuses intents from a
   * Mini App (`allowIntent=false`) and, on its in-app-browser path, opens the
   * intent's `S.browser_fallback_url` INSIDE its own WebView — a single-use
   * launch token spent in the wrong cookie jar (owner research 2026-11-03).
   *
   * Call it from the tap: Telegram ignores it 10 s after the last touch.
   *
   * @param {string} url https URL
   * @param {{ browser?: string }} [options]
   */
  function openExternalLink(url, { browser } = {}) {
    if (openLink.isAvailable()) {
      openLink(url, { tryInstantView: false, ...(browser ? { tryBrowser: browser } : {}) });
    } else {
      window.open(url, '_blank');
    }
  }

  /**
   * Open a t.me link INSIDE the Telegram client, landing the user in the real chat.
   *
   * Deliberately NOT `openLink`/`openExternalLink`: those map to `web_app_open_link`,
   * which renders t.me in the in-app *browser* — a Telegram web page in a webview,
   * which is a dead end for someone trying to reach a human. `openTelegramLink` maps
   * to `web_app_open_tg_link` and switches the user into the chat.
   *
   * Side effect worth knowing: per the SDK contract, this CLOSES the Mini App. That
   * is the intent here (the member goes to talk to the admin), but any copy leading
   * into it should not imply an immediate return trip.
   *
   * The SDK throws on a non-t.me URL and when the env/SDK is unready, hence the
   * isAvailable() gate. Its own fallback is `window.location.href`; we mirror that
   * for the unavailable branch rather than window.open, which Telegram webviews
   * handle unreliably.
   *
   * @param {string} url Full https://t.me/... URL (see constants/support.js).
   */
  function openTelegramChat(url) {
    try {
      if (openTelegramLink.isAvailable()) {
        openTelegramLink(url);
        return;
      }
    } catch (error) {
      console.error('[Platform/TMA] openTelegramLink failed:', error);
    }
    window.location.href = url;
  }

  /**
   * Can this Telegram client ask, in place, to let the bot write to him?
   * `web_app_request_write_access` is Bot API 6.9; an older client says no.
   */
  function canRequestBotWriteAccess() {
    try {
      return requestWriteAccess.isAvailable();
    } catch {
      return false;
    }
  }

  /**
   * Telegram's own "allow the bot to message you" popup, from inside the Mini
   * App — the Mini App stays open (openTelegramChat would close it). Call it
   * from the tap.
   *
   * @returns {Promise<'allowed'|'rejected'|'unavailable'|'failed'>}
   */
  async function requestBotWriteAccess() {
    if (!canRequestBotWriteAccess()) return 'unavailable';
    try {
      // Bounded: a popup that never answers (bridge dropped, app backgrounded)
      // must not hold the caller's button disabled.
      const status = await requestWriteAccess({ timeout: WRITE_ACCESS_TIMEOUT_MS });
      return status === 'allowed' ? 'allowed' : 'rejected';
    } catch (error) {
      console.error('[Platform/TMA] requestWriteAccess failed:', error);
      return 'failed';
    }
  }

  function hapticImpact(style = 'medium') {
    if (hapticFeedback?.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred(style);
    }
  }

  function hapticNotification(type = 'success') {
    if (hapticFeedback?.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred(type);
    }
  }

  function onBackButton(callback) {
    if (backButton?.onClick.isAvailable()) backButton.onClick(callback);
  }

  function offBackButton(callback) {
    if (backButton?.offClick.isAvailable()) backButton.offClick(callback);
  }

  function showBackButton() {
    if (backButton?.show.isAvailable()) backButton.show();
  }

  function hideBackButton() {
    if (backButton?.hide.isAvailable()) backButton.hide();
  }

  function registerServiceWorker() {
    // No-op in TMA — service workers are not used in Telegram Mini App
  }

  /**
   * Boolean push gate — always FALSE inside Telegram. Mirrors the PwaAdapter
   * symmetry (deferred-work 2026-06-03 conformance item #4). Web push has no
   * meaning inside the Telegram webview; the reliable-rail Telegram bot
   * delivers any notifications backed by the same `data` payload (plan Section 3.7).
   */
  function initPushNotifications() {
    return false;
  }

  return {
    type: 'tma',
    initialize,
    getInitData,
    getTelegramUserId,
    openExternalLink,
    openTelegramChat,
    canRequestBotWriteAccess,
    requestBotWriteAccess,
    hapticImpact,
    hapticNotification,
    onBackButton,
    offBackButton,
    showBackButton,
    hideBackButton,
    registerServiceWorker,
    initPushNotifications,
  };
}
